import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { 
  FileText, 
  Plus, 
  Trash2,
  Palette,
  Clock,
  AlertCircle,
  Info,
  Settings as SettingsIcon,
  Save,
  RefreshCw
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

// Icon options for ticket types
const iconOptions = [
  { value: 'FileText', label: 'Document', component: FileText },
  { value: 'AlertCircle', label: 'Alert', component: AlertCircle },
  { value: 'Clock', label: 'Clock', component: Clock },
  { value: 'Settings', label: 'Settings', component: SettingsIcon },
];

const CreateTicketTypeModal = ({ isOpen, onClose, ticketType, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'FileText',
    color: '#3b82f6',
    status: 'active',
    defaultPriority: 'medium',
    slaTargets: {
      response: 1440, // minutes
      resolution: 2880 // minutes
    },
    workflow: {
      enabled: false,
      name: ''
    },
    customFields: [],
    isDefault: false
  });
  
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const colors = [
    { value: '#ef4444', label: 'Red', class: 'bg-red-500' },
    { value: '#3b82f6', label: 'Blue', class: 'bg-blue-500' },
    { value: '#f59e0b', label: 'Orange', class: 'bg-orange-500' },
    { value: '#8b5cf6', label: 'Purple', class: 'bg-purple-500' },
    { value: '#10b981', label: 'Green', class: 'bg-green-500' },
    { value: '#f97316', label: 'Orange Red', class: 'bg-orange-600' },
    { value: '#06b6d4', label: 'Cyan', class: 'bg-cyan-500' },
    { value: '#84cc16', label: 'Lime', class: 'bg-lime-500' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadFormData();
    }
  }, [isOpen, ticketType]);

  const loadFormData = async () => {
    setLoading(true);
    try {
      // Load workflow templates
      const templatesRes = await MockDataService.getWorkflowTemplates?.() || 
        Promise.resolve({ success: true, data: [] });
      
      if (templatesRes.success) {
        setWorkflowTemplates(templatesRes.data || []);
      }

      // Populate form if editing
      if (ticketType) {
        setFormData({
          name: ticketType.name || '',
          description: ticketType.description || '',
          icon: ticketType.icon || 'FileText',
          color: ticketType.color || '#3b82f6',
          status: ticketType.status || 'active',
          defaultPriority: ticketType.defaultPriority || 'medium',
          slaTargets: ticketType.slaTargets || {
            response: 1440,
            resolution: 2880
          },
          workflow: ticketType.workflow || {
            enabled: false,
            name: ''
          },
          customFields: ticketType.customFields || [],
          isDefault: ticketType.isDefault || false
        });
      } else {
        // Reset form for new ticket type
        setFormData({
          name: '',
          description: '',
          icon: 'FileText',
          color: '#3b82f6',
          status: 'active',
          defaultPriority: 'medium',
          slaTargets: {
            response: 1440,
            resolution: 2880
          },
          workflow: {
            enabled: false,
            name: ''
          },
          customFields: [],
          isDefault: false
        });
      }
      setErrors({});
    } catch (err) {
      console.error('Error loading form data:', err);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Ticket Type Name is required';
    }
    
    if (formData.workflow.enabled && !formData.workflow.name) {
      newErrors.workflow = 'Workflow template is required when workflow is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatMinutes = (minutes) => {
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)} hours`;
    return `${Math.floor(minutes / 1440)} days`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const typeData = {
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const response = ticketType 
        ? await MockDataService.updateTicketType(ticketType.id, typeData)
        : await MockDataService.createTicketType(typeData);

      if (response.success) {
        toast.success(ticketType ? 'Ticket type updated successfully' : 'Ticket type created successfully');
        onSave?.(response.data);
        onClose();
        // Reset form
        setFormData({
          name: '',
          description: '',
          icon: 'FileText',
          color: '#3b82f6',
          status: 'active',
          defaultPriority: 'medium',
          slaTargets: {
            response: 1440,
            resolution: 2880
          },
          workflow: {
            enabled: false,
            name: ''
          },
          customFields: [],
          isDefault: false
        });
      } else {
        toast.error(response.message || 'Failed to save ticket type');
      }
    } catch (err) {
      console.error('Error saving ticket type:', err);
      toast.error('Failed to save ticket type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const SelectedIcon = iconOptions.find(opt => opt.value === formData.icon)?.component || FileText;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={ticketType ? 'Edit Ticket Type' : 'Create New Ticket Type'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {loading && !formData.name ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Basic Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="typeName" className="flex items-center gap-2">
                  Ticket Type Name
                  <span className="text-red-500" aria-label="required">*</span>
                </Label>
                <Input
                  id="typeName"
                  placeholder="e.g., Bug, Feature Request, Inquiry"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={errors.name ? 'border-red-500' : ''}
                  aria-required="true"
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This name will be used throughout the system to identify this ticket type
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of this ticket type and when it should be used..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Help users understand when to use this ticket type
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="icon">Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, icon: value }))}
                  >
                    <SelectTrigger id="icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map((icon) => {
                        const IconComponent = icon.component;
                        return (
                          <SelectItem key={icon.value} value={icon.value}>
                            <div className="flex items-center gap-2">
                              <IconComponent className="h-4 w-4" />
                              {icon.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="defaultPriority">Default Priority</Label>
                  <Select
                    value={formData.defaultPriority}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, defaultPriority: value }))}
                  >
                    <SelectTrigger id="defaultPriority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {colors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-10 h-10 rounded-lg ${color.class} border-2 transition-all ${
                        formData.color === color.value
                          ? 'border-gray-900 dark:border-gray-100 ring-2 ring-offset-2 ring-blue-500'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                      aria-label={`Select ${color.label} color`}
                      title={color.label}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Choose a color to visually identify this ticket type
                </p>
              </div>

              {/* Preview */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</p>
                <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-700 rounded-lg">
                  <div
                    className="p-2 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${formData.color}20`, color: formData.color }}
                  >
                    <SelectedIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formData.name || 'Ticket Type Name'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {formData.description || 'Description will appear here'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SLA Targets */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">SLA Targets</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="responseTime">Response Time (minutes)</Label>
                  <Input
                    id="responseTime"
                    type="number"
                    min="0"
                    value={formData.slaTargets.response}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slaTargets: {
                        ...prev.slaTargets,
                        response: parseInt(e.target.value) || 0
                      }
                    }))}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Current: {formatMinutes(formData.slaTargets.response)}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resolutionTime">Resolution Time (minutes)</Label>
                  <Input
                    id="resolutionTime"
                    type="number"
                    min="0"
                    value={formData.slaTargets.resolution}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slaTargets: {
                        ...prev.slaTargets,
                        resolution: parseInt(e.target.value) || 0
                      }
                    }))}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Current: {formatMinutes(formData.slaTargets.resolution)}
                  </p>
                </div>
              </div>
            </div>

            {/* Workflow Configuration */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                <SettingsIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Workflow Configuration</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="workflowEnabled">Enable Workflow</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Apply a workflow template to this ticket type
                    </p>
                  </div>
                  <Switch
                    id="workflowEnabled"
                    checked={formData.workflow.enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      workflow: { ...prev.workflow, enabled: checked }
                    }))}
                  />
                </div>

                {formData.workflow.enabled && (
                  <div className="space-y-2">
                    <Label htmlFor="workflowTemplate" className="flex items-center gap-2">
                      Workflow Template
                      <span className="text-red-500" aria-label="required">*</span>
                    </Label>
                    <Select
                      value={formData.workflow.name}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        workflow: { ...prev.workflow, name: value }
                      }))}
                    >
                      <SelectTrigger id="workflowTemplate" className={errors.workflow ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select workflow template" />
                      </SelectTrigger>
                      <SelectContent>
                        {workflowTemplates.map((template) => (
                          <SelectItem key={template.id || template.name} value={template.id || template.name}>
                            {template.name || template.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.workflow && (
                      <p className="text-sm text-red-500">{errors.workflow}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                <SettingsIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Options</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="status">Active Status</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enable or disable this ticket type
                    </p>
                  </div>
                  <Switch
                    id="status"
                    checked={formData.status === 'active'}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      status: checked ? 'active' : 'inactive'
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="isDefault">Set as Default</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Make this the default ticket type for new tickets
                    </p>
                  </div>
                  <Switch
                    id="isDefault"
                    checked={formData.isDefault}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isDefault: checked }))}
                  />
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  Ticket types help categorize and route tickets efficiently. Configure SLA targets and workflows to streamline processing.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {ticketType ? 'Update Type' : 'Create Type'}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default CreateTicketTypeModal;

